import { useEffect, useState } from 'react'
import { supabase } from '../../config/supabase'
import LoadingSpinner from '../../components/LoadingSpinner'

/**
 * Rota dedicada para callback de autenticação em Popup.
 * Esta página é carregada DENTRO da janelinha (popup).
 * Responsabilidade:
 * 1. Verificar se a sessão foi criada.
 * 2. Notificar a janela pai (opener).
 * 3. Fechar a si mesma.
 */
const PopupCallback = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('Finalizando autenticação...')

  useEffect(() => {
    let mounted = true
    let timeoutId: NodeJS.Timeout

    const handleSuccess = () => {
      if (!mounted) return
      console.log('✅ PopupCallback: Login sucesso! Notificando janela pai...')
      setStatus('success')
      setMessage('Login realizado com sucesso! Fechando janela...')

      // 1. Limpar flag de pendência
      window.localStorage.removeItem('overpixel_auth_popup_pending')

      // 2. BroadcastChannel (Comunicação moderna)
      try {
        const channel = new BroadcastChannel('auth_channel')
        channel.postMessage({ type: 'AUTH_CALLBACK_SUCCESS' })
        // Dar um tempinho para a mensagem sair antes de fechar o canal
        setTimeout(() => channel.close(), 100)
      } catch (e) {
        console.error('Erro BroadcastChannel:', e)
      }

      // 3. LocalStorage Trigger (Nuclear Fallback - funciona sempre)
      // O evento 'storage' dispara em outras abas/janelas do mesmo domínio
      window.localStorage.setItem('overpixel_auth_success_trigger', Date.now().toString())

      // 4. window.opener (Fallback clássico)
      if (window.opener) {
        try {
          window.opener.postMessage({ type: 'AUTH_CALLBACK_SUCCESS' }, window.location.origin)
        } catch (e) {
          console.warn('Erro ao postar mensagem para opener:', e)
        }
      }

      // 5. Fechar a janela
      // Pequeno delay para garantir que os eventos foram disparados e processados
      setTimeout(() => {
        window.close()
        // Se não fechar (bloqueio do browser), avisa o usuário
        if (!window.closed) {
             setMessage('Pode fechar esta janela agora.')
        }
      }, 500)
    }

    // Monitorar estado da autenticação
    const checkSession = async () => {
      // 0. Tenta processar hash manualmente se o supabase não pegar (CRÍTICO PARA POPUP)
      let hash = window.location.hash
      // Remover o # inicial se existir
      if (hash && hash.startsWith('#')) {
        hash = hash.substring(1)
      }

      if (hash && hash.includes('access_token')) {
        console.log('🔍 PopupCallback: Hash com tokens detectado. Tentando processar manualmente...')
        const params = new URLSearchParams(hash)
        const access_token = params.get('access_token')
        const refresh_token = params.get('refresh_token')
        const type = params.get('type')
        
        if (access_token) {
           console.log('🔑 Tokens extraídos. Definindo sessão...')
           const { error } = await supabase.auth.setSession({ 
             access_token, 
             refresh_token: refresh_token || '' 
           })
           
           if (!error) {
             console.log('✅ Sessão definida manualmente via hash!')
             handleSuccess()
             return
           } else {
             console.warn('⚠️ Falha ao definir sessão via hash:', error)
             // Tentar sucesso mesmo assim, pois os tokens existem
             if (type === 'recovery' || type === 'magiclink' || type === 'signup') {
                // Fluxos que podem não ter refresh token ou comportar diferente
             }
           }
        }
      }

      // O Supabase processa o hash da URL automaticamente ao inicializar
      // Mas podemos ouvir explicitamente
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        console.error('Erro na sessão:', error)
        // Não mostrar erro imediatamente se tivermos hash para processar, mas aqui já processamos
        // Se falhou tudo, mostra erro
        setStatus('error')
        setMessage(`Erro ao autenticar: ${error.message}`)
        return
      }

      if (session) {
        console.log('✅ Sessão detectada via getSession!')
        handleSuccess()
      } else {
        // Se não tem sessão, aguarda o listener do supabase
        console.log('⏳ Aguardando onAuthStateChange...')
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            console.log('🔔 Evento Auth:', event)
            if (event === 'SIGNED_IN' || session) {
                handleSuccess()
            }
        })
        
        // Timeout de segurança
        timeoutId = setTimeout(() => {
             if (status !== 'success') {
                 // Tenta verificar uma última vez
                 supabase.auth.getSession().then(({ data }) => {
                     if (data.session) handleSuccess()
                     else {
                         console.error('❌ Timeout de autenticação')
                         setStatus('error')
                         setMessage('Tempo limite excedido. A autenticação demorou muito.')
                     }
                 })
             }
        }, 15000) // Aumentado para 15s

        return () => subscription.unsubscribe()
      }
    }

    checkSession()

    return () => {
      mounted = false
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [])

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white p-4">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4 animate-bounce">
          <svg className="w-8 h-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold mb-2">Conectado!</h2>
        <p className="text-gray-400 mb-6 text-center max-w-md">{message}</p>
        <button 
          onClick={() => window.close()}
          className="px-6 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors font-medium"
        >
          Fechar Janela
        </button>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white p-4">
        <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="text-xl font-bold mb-2">Erro no Login</h2>
        <p className="text-red-400 mb-6 text-center max-w-md">{message}</p>
        <button 
          onClick={() => window.close()}
          className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
        >
          Fechar
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
        <LoadingSpinner size="large" message={message} />
    </div>
  )
}

export default PopupCallback
